/**
 * Reports API Client
 * Executive dashboard reports (Phase 8)
 */

import { apiFetch } from './config';

export interface EngineeringInvestmentReport {
  report_type: string;
  period: string;
  period_start: string;
  period_end: string;
  summary: {
    total_commits: number;
    total_contributors: number;
    total_repos: number;
    total_lines_changed: number;
  };
  investment_by_module: Array<{
    module: string;
    commit_count: number;
    lines_added: number;
    lines_removed: number;
    contributors: number;
    commit_percentage: number;
    churn_percentage: number;
  }>;
  top_contributors: Array<{
    developer_name: string;
    developer_email: string;
    total_commits: number;
    total_lines_added: number;
    total_prs_merged: number;
    avg_compliance: number;
  }>;
  repository_breakdown: Array<{
    repo_name: string;
    commit_count: number;
    contributors: number;
    lines_added: number;
  }>;
}

export interface StandardsRoiReport {
  report_type: string;
  period: string;
  period_start: string;
  period_end: string;
  summary: {
    avg_compliance_score: string;
    total_commits: number;
    compliant_commits: number;
    compliance_rate: string;
    anti_pattern_commits: number;
    anti_pattern_rate: string;
    standards_shown: number;
  };
  adoption_trend: Array<{
    week: string;
    sessions_with_standards: number;
    standards_shown: number;
    avg_relevance: number;
  }>;
  compliance_by_developer: Array<{
    developer_email: string;
    developer_name: string;
    total_commits: number;
    compliant_commits: number;
    avg_compliance_score: number;
    anti_pattern_commits: number;
  }>;
  top_standards: Array<{
    standard_name: string;
    category: string;
    times_shown: number;
    unique_sessions: number;
    avg_relevance: number;
  }>;
  anti_patterns_detected: Array<{
    pattern_name: string;
    severity: string;
    occurrences: number;
    repos_affected: number;
  }>;
  insights: Array<{
    type: 'positive' | 'attention' | 'warning';
    message: string;
    developers?: string[];
    patterns?: string[];
  }>;
}

export interface AiLeverageReport {
  report_type: string;
  period: string;
  period_start: string;
  period_end: string;
  summary: {
    total_sessions: number;
    unique_ai_users: number;
    avg_session_minutes: string;
    ai_assisted_commits: number;
    total_commits: number;
    ai_commit_percentage: string;
    productivity_multiplier: string;
  };
  comparison: {
    ai_assisted: {
      developer_count: number;
      avg_commits_per_dev: string;
      avg_lines_per_dev: string;
      avg_compliance: string;
    };
    non_ai: {
      developer_count: number;
      avg_commits_per_dev: string;
      avg_lines_per_dev: string;
      avg_compliance: string;
    };
  };
  standards_effectiveness: Array<{
    standard_name: string;
    category: string;
    sessions_shown: number;
    related_commits: number;
    subsequent_compliance: number;
  }>;
  insights: Array<{
    type: 'positive' | 'info' | 'warning';
    message: string;
  }>;
}

export interface RiskForecastReport {
  report_type: string;
  generated_at: string;
  overall_risk_score: number;
  risk_level: 'low' | 'moderate' | 'elevated' | 'high' | 'critical';
  summary: {
    inactive_developers: number;
    high_velocity_devs: number;
    critical_silos: number;
    stale_prs: number;
    active_alerts: number;
    high_churn_files: number;
  };
  risks: {
    inactive_developers: Array<{
      email: string;
      name: string;
      days_inactive: number;
      last_commit: string;
      risk: string;
    }>;
    burnout_risk: Array<{
      email: string;
      name: string;
      commits_7d: number;
      weekend_commits: number;
      after_hours: number;
    }>;
    knowledge_silos: Array<{
      module_path: string;
      primary_contributor: string;
      contribution_percentage: number;
      total_contributors: number;
      risk_level: string;
      repo_name: string;
    }>;
    review_bottleneck: Array<{
      repo: string;
      pr_number: number;
      title: string;
      author: string;
      days_open: number;
    }>;
    high_churn_files: Array<{
      file_path: string;
      change_count: number;
      unique_contributors: number;
      last_modified_by: string;
      repo_name: string;
    }>;
  };
  active_alerts: Array<{
    alert_type: string;
    severity: string;
    message: string;
    developer_email: string;
    created_at: string;
    project_name: string;
  }>;
  recommendations: Array<{
    priority: 'critical' | 'high' | 'medium' | 'low';
    category: string;
    action: string;
    affected?: string[];
    details?: string;
  }>;
}

export type ReportPeriod = '7d' | '30d' | '90d';

/**
 * Unwrap the backend response envelope.
 * apiFetch returns { success, data: fullBody } where fullBody = { success, message, data: payload, meta }
 * We need to extract the inner 'data' field.
 */
function unwrapResponse<T>(response: { success: boolean; data?: any; error?: string }): { success: boolean; data?: T; error?: string } {
  if (response.success && response.data) {
    const innerData = response.data?.data !== undefined ? response.data.data : response.data;
    return { success: true, data: innerData as T };
  }
  return { success: false, error: response.error };
}

/**
 * Fetch engineering investment report
 */
export const getEngineeringInvestment = async (
  period: ReportPeriod = '30d',
  companyId?: string
): Promise<{ success: boolean; data?: EngineeringInvestmentReport; error?: string }> => {
  const params = new URLSearchParams({ period });
  if (companyId) params.append('Company_ID', companyId);

  const response = await apiFetch<any>(`/api/reports/engineering-investment?${params}`);
  return unwrapResponse<EngineeringInvestmentReport>(response);
};

/**
 * Fetch standards ROI report
 */
export const getStandardsRoi = async (
  period: ReportPeriod = '30d',
  companyId?: string
): Promise<{ success: boolean; data?: StandardsRoiReport; error?: string }> => {
  const params = new URLSearchParams({ period });
  if (companyId) params.append('Company_ID', companyId);

  const response = await apiFetch<any>(`/api/reports/standards-roi?${params}`);
  return unwrapResponse<StandardsRoiReport>(response);
};

/**
 * Fetch AI leverage report
 */
export const getAiLeverage = async (
  period: ReportPeriod = '30d',
  companyId?: string
): Promise<{ success: boolean; data?: AiLeverageReport; error?: string }> => {
  const params = new URLSearchParams({ period });
  if (companyId) params.append('Company_ID', companyId);

  const response = await apiFetch<any>(`/api/reports/ai-leverage?${params}`);
  return unwrapResponse<AiLeverageReport>(response);
};

/**
 * Fetch risk forecast report
 */
export const getRiskForecast = async (
  companyId?: string
): Promise<{ success: boolean; data?: RiskForecastReport; error?: string }> => {
  const params = new URLSearchParams();
  if (companyId) params.append('Company_ID', companyId);

  const query = params.toString();
  const response = await apiFetch<any>(`/api/reports/risk-forecast${query ? `?${query}` : ''}`);
  return unwrapResponse<RiskForecastReport>(response);
};
